import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Timer } from "lucide-react";

const FIELDS = [
  { key: "grace_seconds", label: "Loading grace (seconds)", hint: "Ignored at the start of every session" },
  { key: "afk_seconds", label: "AFK cutoff (seconds)", hint: "Points pause after this long without moving" },
  { key: "movement_threshold", label: "Movement threshold", hint: "Game units that count as having moved" },
  { key: "session_gap_seconds", label: "Session gap (seconds)", hint: "Longer absence starts a fresh session" },
  { key: "max_minutes_per_credit", label: "Max minutes per credit", hint: "Safety cap against clock jumps" },
  { key: "activity_window_seconds", label: "Activity window (seconds)", hint: "How long a movement ping counts for" },
];

export const PlaytimeRules = () => {
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/playtime-config");
      setCfg(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await api.put("/admin/playtime-config", {
        grace_seconds: Number(cfg.grace_seconds),
        session_gap_seconds: Number(cfg.session_gap_seconds),
        max_minutes_per_credit: Number(cfg.max_minutes_per_credit),
        require_activity: !!cfg.require_activity,
        activity_window_seconds: Number(cfg.activity_window_seconds),
        require_movement: !!cfg.require_movement,
        afk_seconds: Number(cfg.afk_seconds),
        movement_threshold: Number(cfg.movement_threshold),
      });
      toast.success("Playtime rules saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!cfg) return null;

  return (
    <div className="mt-4 border border-border bg-card p-8" data-testid="playtime-rules-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Timer className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Playtime rules</h3>
        </div>
        <Button className="rounded-none" onClick={save} data-testid="save-playtime-rules-btn">
          Save rules
        </Button>
      </div>

      <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
        Points accrue from real elapsed seconds and bank the remainder, so polling more often can never
        inflate anyone's total. The grace window discards the start of each session, which is where
        loading screens and character select live.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{f.label}</Label>
            <Input
              type="number"
              data-testid={`playtime-${f.key}`}
              className="mt-2 rounded-none bg-secondary mono"
              value={cfg[f.key] ?? 0}
              onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
            />
            <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-start gap-4 border-t border-border pt-6">
        <Switch
          data-testid="playtime-require-movement"
          checked={!!cfg.require_movement}
          onCheckedChange={(v) => setCfg({ ...cfg, require_movement: v })}
        />
        <div>
          <div className="font-semibold">Require movement before points start</div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Apollo reports each player's ped position. With this on, once the loading grace expires a
            player must physically move at least once before any points accrue — so nobody farms from
            the loading screen or character select. Points then pause again after the AFK cutoff of
            zero movement and resume the moment they move.
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-start gap-4 border-t border-border pt-6">
        <Switch
          data-testid="playtime-require-activity"
          checked={!!cfg.require_activity}
          onCheckedChange={(v) => setCfg({ ...cfg, require_activity: v })}
        />
        <div>
          <div className="font-semibold">Also require a Lua activity ping (legacy)</div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Only needed if you install the Lua resource and want its own AFK detection on top of the
            position check above. Leave this off — position tracking already handles AFK.
          </p>
        </div>
      </div>
    </div>
  );
};
